import React, { useState, useEffect, useRef } from 'react';

interface AnimatedStarsProps {
  value: number;
  lastReward?: number; // Amount that was just added
  onAnimationComplete?: () => void;
}

const AnimatedStars: React.FC<AnimatedStarsProps> = ({
  value,
  lastReward,
  onAnimationComplete
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const prevValueRef = useRef(value);
  const animationRef = useRef<number | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);

  // Audio context for star sound effects
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  };

  const playStarSound = () => {
    try {
      const audioContext = getAudioContext();

      // Create a magical, twinkling star sound
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const oscillator3 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();

      // Use higher, sparkly frequencies (pentatonic scale for pleasant harmony)
      oscillator1.frequency.setValueAtTime(1047, audioContext.currentTime); // C6
      oscillator2.frequency.setValueAtTime(1175, audioContext.currentTime); // D6
      oscillator3.frequency.setValueAtTime(1319, audioContext.currentTime); // E6

      // Add a sparkling frequency sweep
      oscillator1.frequency.exponentialRampToValueAtTime(1568, audioContext.currentTime + 0.3); // G6
      oscillator2.frequency.exponentialRampToValueAtTime(1760, audioContext.currentTime + 0.3); // A6
      oscillator3.frequency.exponentialRampToValueAtTime(2093, audioContext.currentTime + 0.3); // C7

      // Use triangle waves for a softer, more magical tone
      oscillator1.type = 'triangle';
      oscillator2.type = 'triangle';
      oscillator3.type = 'triangle';

      // Add a high-pass filter for sparkle
      filter.type = 'highpass';
      filter.frequency.setValueAtTime(800, audioContext.currentTime);
      filter.Q.setValueAtTime(2, audioContext.currentTime);

      // Quick, sparkling envelope like a star twinkling
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5);

      // Connect the audio graph
      oscillator1.connect(filter);
      oscillator2.connect(filter);
      oscillator3.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioContext.destination);

      const now = audioContext.currentTime;
      oscillator1.start(now);
      oscillator2.start(now);
      oscillator3.start(now);
      oscillator1.stop(now + 0.5);
      oscillator2.stop(now + 0.5);
      oscillator3.stop(now + 0.5);
    } catch (error) {
      console.log('Audio not available:', error);
    }
  };

  useEffect(() => {
    // On first mount, just set the initial value without animation
    if (!isInitialized) {
      setDisplayValue(value);
      setIsInitialized(true);
      prevValueRef.current = value;
      return;
    }

    const prevValue = prevValueRef.current;
    const difference = value - prevValue;

    if (difference !== 0) {
      // Start animation for both increases and decreases
      setIsAnimating(true);
      if (difference > 0) setShowReward(true);

      const duration = 1200; // ms
      const startTime = Date.now();
      const startValue = prevValue;
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 2);
        const currentValue = Math.round(startValue + (difference * easeOut));
        setDisplayValue(currentValue);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
          onAnimationComplete?.();
        }
      };
      animationRef.current = requestAnimationFrame(animate);

      if (difference > 0) {
        setTimeout(() => setShowReward(false), 1500);
      }
    } else {
      setDisplayValue(value);
    }

    prevValueRef.current = value;

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, onAnimationComplete, isInitialized]);

  const handleClick = () => {
    setIsClicked(true);
    playStarSound();
    // Reset click animation after it completes
    setTimeout(() => setIsClicked(false), 600);
  };

  return (
    <div
      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer', overflow: 'visible' }}
      onClick={handleClick}
    >
      {/* Main Stars Display */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          fontWeight: 800,
          fontSize: '1.4rem',
          color: (isAnimating || isClicked) ? '#FFD700' : '#FFFF00',
          transition: 'all 0.3s ease',
          transform: isAnimating
            ? 'scale(1.1)'
            : isClicked
            ? 'scale(1.15) rotate(-5deg)'
            : 'scale(1)',
          textShadow: isAnimating
            ? '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.4), 0 0 5px rgba(255, 255, 255, 0.8)'
            : isClicked
            ? '0 0 15px rgba(255, 215, 0, 0.9), 0 0 25px rgba(255, 215, 0, 0.5), 0 0 8px rgba(255, 255, 255, 0.9)'
            : '0 0 5px rgba(255, 255, 255, 0.6), 0 0 10px rgba(255, 255, 255, 0.3)'
        }}
      >
        <span
          role="img"
          aria-label="Stars"
          style={{
            marginRight: 8,
            fontSize: '1.8rem',
            animation: isAnimating
              ? 'starTwinkle 0.8s ease-out'
              : isClicked
              ? 'starSpin 0.6s ease-out'
              : undefined
          }}
        >
          ⭐
        </span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            animation: isAnimating
              ? 'numberPulse 0.8s ease-out'
              : isClicked
              ? 'clickNumberShake 0.6s ease-out'
              : undefined,
            display: 'inline-block',
            minWidth: 'auto',
            lineHeight: '1',
            verticalAlign: 'baseline'
          }}
        >
          {displayValue.toLocaleString()}
        </span>
      </div>

      {/* Floating +Stars Animation */}
      {showReward && lastReward && lastReward > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            background: 'linear-gradient(135deg, #9c88ff 0%, #6b46c1 100%)',
            color: 'white',
            borderRadius: '20px',
            padding: '6px 12px',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(156, 136, 255, 0.4)',
            animation: 'floatUp 1.5s ease-out forwards',
            zIndex: 1000,
            border: '2px solid rgba(255, 255, 255, 0.3)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none'
          }}
        >
          +{lastReward} ⭐
        </div>
      )}

      {/* Star Sparkle Effects */}
      {(isAnimating || isClicked) && (
        <>
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${15 + (i * 8)}%`,
                left: `${5 + (i * 12)}%`,
                fontSize: '0.8rem',
                animation: isClicked
                  ? `clickStarSparkle${i % 4} 0.6s ease-out ${i * 0.05}s`
                  : `starSparkle${i % 4} 0.8s ease-out ${i * 0.08}s`,
                zIndex: 999,
                pointerEvents: 'none'
              }}
            >
              ✨
            </div>
          ))}
        </>
      )}

      {/* Click Effect - Star Burst */}
      {isClicked && (
        <>
          {[...Array(6)].map((_, i) => (
            <div
              key={`star-burst-${i}`}
              style={{
                position: 'absolute',
                top: i % 2 === 0 ? '15%' : '85%',
                left: i % 3 === 0 ? '5%' : i % 3 === 1 ? '95%' : '50%',
                fontSize: '1rem',
                animation: `starBurst${i % 4} 0.8s ease-out ${i * 0.04}s`,
                zIndex: 1000,
                pointerEvents: 'none'
              }}
            >
              ⭐
            </div>
          ))}
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes starTwinkle {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.3) rotate(90deg); }
          50% { transform: scale(1.1) rotate(180deg); }
          75% { transform: scale(1.2) rotate(270deg); }
        }

        @keyframes starSpin {
          0% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.4) rotate(180deg); }
          100% { transform: scale(1) rotate(360deg); }
        }

        @keyframes numberPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        @keyframes floatUp {
          0% {
            transform: translateY(0px) scale(0.8);
            opacity: 0;
          }
          20% {
            transform: translateY(-10px) scale(1.1);
            opacity: 1;
          }
          100% {
            transform: translateY(-40px) scale(0.9);
            opacity: 0;
          }
        }

        @keyframes starSparkle0 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.5) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes starSparkle1 {
          0% {
            transform: scale(0) translateX(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.2) translateX(-15px) rotate(-180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateX(-30px) rotate(-360deg);
            opacity: 0;
          }
        }

        @keyframes starSparkle2 {
          0% {
            transform: scale(0) translateY(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.3) translateY(-20px) rotate(270deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateY(-40px) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes starSparkle3 {
          0% {
            transform: scale(0) translateX(0px) translateY(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.4) translateX(15px) translateY(-10px) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateX(30px) translateY(-20px) rotate(360deg);
            opacity: 0;
          }
        }

        /* Click Animation Keyframes */
        @keyframes clickNumberShake {
          0%, 100% { transform: translateX(0); }
          10% { transform: translateX(-2px) scale(1.05); }
          20% { transform: translateX(2px) scale(1.1); }
          30% { transform: translateX(-2px) scale(1.05); }
          40% { transform: translateX(2px) scale(1.1); }
          50% { transform: translateX(-1px) scale(1.05); }
          60% { transform: translateX(1px) scale(1.1); }
          70% { transform: translateX(-1px) scale(1.05); }
          80% { transform: translateX(1px) scale(1.1); }
          90% { transform: translateX(0) scale(1.05); }
        }

        @keyframes clickStarSparkle0 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(2) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes clickStarSparkle1 {
          0% {
            transform: scale(0) translateX(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.8) translateX(-20px) rotate(-180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateX(-40px) rotate(-360deg);
            opacity: 0;
          }
        }

        @keyframes clickStarSparkle2 {
          0% {
            transform: scale(0) translateY(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(2.2) translateY(-25px) rotate(270deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateY(-50px) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes clickStarSparkle3 {
          0% {
            transform: scale(0) translateX(0px) translateY(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(2) translateX(20px) translateY(-15px) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateX(40px) translateY(-30px) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes starBurst0 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(-35px, -25px) scale(1.3) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: translate(-70px, 50px) scale(0.2) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes starBurst1 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(35px, -25px) scale(1.3) rotate(-180deg);
            opacity: 1;
          }
          100% {
            transform: translate(70px, 50px) scale(0.2) rotate(-720deg);
            opacity: 0;
          }
        }

        @keyframes starBurst2 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(-25px, -35px) scale(1.3) rotate(270deg);
            opacity: 1;
          }
          100% {
            transform: translate(-50px, 70px) scale(0.2) rotate(1080deg);
            opacity: 0;
          }
        }

        @keyframes starBurst3 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(25px, -35px) scale(1.3) rotate(-270deg);
            opacity: 1;
          }
          100% {
            transform: translate(50px, 70px) scale(0.2) rotate(-1080deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedStars;